// The playoff picture, derived rather than stored.
//
// The previous design wrote a bracket into playoffs.championship_bracket as
// JSON when the playoffs began, then mutated it each week through an admin
// endpoint. Three problems, all structural:
//
//   1. It did not exist until an admin ran initializePlayoffs, so there was
//      nothing to show during the regular season. A manager could not see
//      where they would land if the playoffs started today, which is the
//      question that makes weeks 8 to 10 worth playing.
//   2. It denormalised member objects into the JSON and keyed them by user_id,
//      while the frontend looks members up by league_members.id. Those never
//      matched, so the bracket could not resolve a manager.
//   3. Stored state drifts. If a week was rescored, the bracket kept the old
//      answer.
//
// Everything here is a pure function of two immutable inputs: the standings
// order, and weekly_standings points per playoff week. Both are already
// written by the scorer, so the bracket is always current and always agrees
// with the standings beside it.
import {
  PLAYOFF_TEAMS,
  FIRST_PLAYOFF_WEEK,
  TOTAL_LINEUP_WEEKS,
  REGULAR_SEASON_WEEKS,
} from "./season";

export const PLAYOFF_WEEKS = Array.from(
  { length: TOTAL_LINEUP_WEEKS - REGULAR_SEASON_WEEKS },
  (_, i) => FIRST_PLAYOFF_WEEK + i
);

export const [ROUND_ONE_WEEK, SEMIS_WEEK, FINAL_WEEK] = PLAYOFF_WEEKS;

// A tie goes to the better seed. Somebody has to advance, and rewarding the
// regular season is the only tiebreak that is not arbitrary. `home` is always
// the better seed, which is what makes this readable at the call site.
const winnerOf = (match) => {
  if (match.homePoints === null || match.awayPoints === null) return null;
  return match.homePoints >= match.awayPoints ? match.home : match.away;
};

const loserOf = (match) => {
  const winner = winnerOf(match);
  if (!winner) return null;
  return winner === match.home ? match.away : match.home;
};

const makeMatch = (week, home, away, pointsByWeek) => {
  const points = pointsByWeek?.[week] || {};
  const played = Boolean(pointsByWeek?.[week]);
  const match = {
    week,
    home: home || null,
    away: away || null,
    homePoints: played && home ? points[home.id] ?? 0 : null,
    awayPoints: played && away ? points[away.id] ?? 0 : null,
    winner: null,
    played,
  };
  // A match cannot resolve until both sides are known, which is how an
  // unplayed semi-final renders as "winner of round one" rather than a bye.
  if (played && home && away) match.winner = winnerOf(match);
  return match;
};

/**
 * The full playoff picture, for any point in the season.
 *
 * @param {Array<{id: string, teamName?: string, firstName?: string}>} standings
 *        Managers in finishing order, best first. During the regular season
 *        this is the live table, which makes the result a projection.
 * @param {Object<number, Object<string, number>>} pointsByWeek
 *        Points per member id, keyed by lineup week. A week absent from this
 *        map has not been played, so its matches stay unresolved.
 * @returns {{seeds, bracket, pool, projected}}
 */
export function buildPlayoffPicture(standings = [], pointsByWeek = {}) {
  const ordered = (standings || []).filter(Boolean);

  const seeds = ordered.slice(0, PLAYOFF_TEAMS).map((member, i) => ({
    ...member,
    seed: i + 1,
  }));

  // Everyone below the line. No bracket and no seeding: one pool over the same
  // three weeks, highest total takes next season's first pick.
  const poolMembers = ordered.slice(PLAYOFF_TEAMS);

  const [s1, s2, s3, s4, s5, s6] = seeds;

  // Round one: seeds 1 and 2 rest. This is the only field that fills three
  // weeks, which is why PLAYOFF_TEAMS does not vary with league size.
  const r1a = makeMatch(ROUND_ONE_WEEK, s3, s6, pointsByWeek);
  const r1b = makeMatch(ROUND_ONE_WEEK, s4, s5, pointsByWeek);

  // The top seed meets the lowest surviving seed, so beating the bye is worth
  // something. Falls back to bracket order before round one has been played.
  const survivors = [winnerOf(r1a), winnerOf(r1b)].filter(Boolean);
  const lowestSurvivor = survivors.length === 2
    ? survivors.reduce((low, m) => (m.seed > low.seed ? m : low))
    : null;
  const otherSurvivor = lowestSurvivor
    ? survivors.find((m) => m !== lowestSurvivor)
    : null;

  const semiA = makeMatch(SEMIS_WEEK, s1, lowestSurvivor, pointsByWeek);
  const semiB = makeMatch(SEMIS_WEEK, s2, otherSurvivor, pointsByWeek);

  const final = makeMatch(FINAL_WEEK, winnerOf(semiA), winnerOf(semiB), pointsByWeek);
  const thirdPlace = makeMatch(FINAL_WEEK, loserOf(semiA), loserOf(semiB), pointsByWeek);

  // Sort is stable and poolMembers arrives in standings order, so a tie on
  // pool points falls to the better regular season finish. Deliberately not
  // alphabetical: before a playoff game is played everyone is on zero, and
  // ordering that by name would rank the pool by team name.
  const poolTotals = poolMembers
    .map((member) => ({
      ...member,
      total: PLAYOFF_WEEKS.reduce(
        (sum, week) => sum + (pointsByWeek?.[week]?.[member.id] ?? 0),
        0
      ),
    }))
    .sort((a, b) => b.total - a.total);

  const anyPlayed = PLAYOFF_WEEKS.some((week) => Boolean(pointsByWeek?.[week]));

  return {
    seeds,
    bracket: {
      roundOne: [r1a, r1b],
      semis: [semiA, semiB],
      final,
      thirdPlace,
      byes: [s1, s2].filter(Boolean),
      champion: winnerOf(final),
    },
    pool: {
      participants: poolTotals,
      // Decided only once every playoff week has been scored, so a leader
      // mid-run is never presented as the winner.
      leader: poolTotals[0] || null,
      decided: PLAYOFF_WEEKS.every((week) => Boolean(pointsByWeek?.[week])),
      prize: "First pick in next season's draft",
    },
    // True while this is still a forecast: the seeds can change until the
    // regular season is over.
    projected: !anyPlayed,
  };
}
